import React from 'react'
import { useConfig } from '../../routes'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import { useWindowSize } from 'customer/hooks/useWindowSize'
import { styleTheme } from '../../style/theme'
import { getPrimaryColor } from '../../style/utils'
import { format, parse } from 'date-fns'
import ja from 'date-fns/locale/ja'

import { ParticipationNewContainerProps, ParticipationNewProps } from './ParticipationNew.types'
import { Form } from './components/Form'
import { BridalFairPageItemType } from './components/Form/Form.types'
import { pricingLabels } from '../BridalFairs/BridalFairs.types'
import { useParticipationNew } from './hooks/useParticipationNew'
import { ImageComponent } from '../../components/ui-elements/Image'

type ConfigType = {
  primaryColor: string
  fair?: {
    bridalFairDetailKey: string
    bridalFairDetailName: string
    bridalFairDetailMainImageUrl: string
    bridalFairDetailPricing: 'free' | 'paid' | 'partially_paid'
    fairTimeStartAt: string
    fairTimeEndAt: string
    fairTimeAvailability: 'none' | 'few' | 'lot'
  }
  bridalFairPageItems?: BridalFairPageItemType[]
  tenant?: {
    privacyPolicyLink?: string
  }
}

const MOBILE_BREAKPOINT = 520

const startAtToString = (date: string) => {
  // YYYY-MM-DD
  if (new RegExp('\\d{4}-\\d{2}-\\d{2}').test(date)) {
    return format(new Date(date), 'yyyy/M/d (eee)', { locale: ja })
  }
  // YYYYMMDDHHmm
  if (new RegExp('^\\d{4}\\d{2}\\d{2}\\d{4}$').test(date)) {
    return format(parse(date, 'yyyyMMddHHmm', new Date()), 'yyyy/M/d (eee)', { locale: ja })
  }
  return ''
}

export const ParticipationNew: React.FC<ParticipationNewContainerProps> = (props) => {
  const { primaryColor, tenant, bridalFairPageItems } = useConfig() as ConfigType
  const { bridalFairDetailKey, startAt } = useParams() as { bridalFairDetailKey: string; startAt: string }
  const { bridalFairDetail, fairTimeOptions } = useParticipationNew({
    bridalFairDetailKey,
    startAt,
  })
  return (
    <StyledPage
      {...props}
      primaryColor={primaryColor || '#1babbc'}
      bridalFairDetail={bridalFairDetail}
      privacyPolicyLink={tenant?.privacyPolicyLink}
      bridalFairPageItems={bridalFairPageItems}
      fairTimeOptions={fairTimeOptions}
      startAt={startAt}
    />
  )
}

const Page: React.FC<ParticipationNewProps> = ({
  className,
  primaryColor,
  bridalFairDetail,
  privacyPolicyLink,
  bridalFairPageItems,
  fairTimeOptions,
  startAt,
}) => {
  const { width } = useWindowSize()

  return (
    <div className={className}>
      <div className="title">フェア予約</div>
      <div className="fair-detail-wrapper">
        {bridalFairDetail?.mainImageData && (
          <div className="fair-image-wrapper">
            <ImageComponent
              imageData={bridalFairDetail.mainImageData}
              aspectRatio={width <= MOBILE_BREAKPOINT ? 'oneToOneImage' : 'threeToTwoImage'}
              altText="フェア画像"
            />
          </div>
        )}
        <div className="fair-detail">
          <div className="tag-wrapper">
            <span className="tag">予約内容</span>
            <span className="pricing-tag">{pricingLabels[bridalFairDetail?.pricing || 'free']}</span>
          </div>
          <div className="fair-name">{bridalFairDetail?.name || ''}</div>
          <div className="fair-time">{startAtToString(startAt)}</div>
        </div>
      </div>
      <p className="description">ご予約にあたり以下の情報をご記入ください。</p>
      <Form
        primaryColor={primaryColor}
        disabled={false} // TODO:未使用なので削除する
        privacyPolicyLink={privacyPolicyLink}
        fairTimeOptions={fairTimeOptions}
        bridalFairPageItems={
          bridalFairPageItems || [
            { fieldType: 'name', name: 'お名前', order: 0, isRequired: true, placeholder: '' },
            { fieldType: 'name_kana', name: 'お名前（ふりがな）', order: 1, isRequired: true, placeholder: '' },
            { fieldType: 'email', name: 'メールアドレス', order: 2, isRequired: true, placeholder: '' },
            { fieldType: 'phone_number', name: '電話番号', order: 3, isRequired: false, placeholder: '' },
            { fieldType: 'gender', name: '性別', order: 4, isRequired: false, placeholder: '' },
            { fieldType: 'number_of_people', name: '参加人数', order: 5, isRequired: false, placeholder: '' },
            { fieldType: 'content', name: 'ご質問・ご要望など', order: 6, isRequired: false, placeholder: '' },
          ]
        }
      />
    </div>
  )
}

const StyledPage = styled(Page)`
  display: grid;
  gap: 40px;
  max-width: 768px;
  margin: 0 auto;
  background-color: ${styleTheme.colors.white};
  padding: 32px 16px;
  color: ${styleTheme.colors.black};

  > .title {
    color: ${getPrimaryColor};
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 16px;
  }

  > .description {
    font-size: 14px;
    color: #585858;
    line-height: 1.75;
  }

  > .fair-detail-wrapper {
    display: flex;
    height: 200px;
    border: 1px solid ${styleTheme.colors.gray.base};
    overflow: hidden;
    @media (max-width: 768px) {
      height: fit-content;
    }

    > .fair-image-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      aspect-ratio: 3 / 2;
      overflow: hidden;

      @media (max-width: 768px) {
        width: 40%;
        aspect-ratio: 3 / 2;
      }

      @media (max-width: 480px) {
        width: 50%;
        aspect-ratio: 3 / 2;
      }

      > img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;

        @media (max-width: 768px) {
          width: 100%;
          height: auto;
          object-fit: contain;
        }
      }
    }

    > .fair-detail {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 32px;
      gap: 4px;
      height: 200px;

      @media (max-width: 768px) {
        height: 128px;
        padding: 12px;
      }

      > .tag-wrapper {
        display: flex;
        gap: 4px;
        @media (min-width: 769px) {
          margin-bottom: 10px;
        }

        > span {
          color: ${getPrimaryColor};
          border: solid 1px ${getPrimaryColor};
          border-radius: 2px;
          font-size: 12px;
          padding: 0 8px;
          line-height: 1.5;
          width: fit-content;
        }

        > .pricing-tag {
          color: ${styleTheme.colors.black};
          border: solid 1px ${styleTheme.colors.black};
        }
      }

      > .fair-name {
        font-size: 16px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        height: 48px;
        @media (max-width: 768px) {
          font-size: 14px;
          height: 44px;
        }
      }

      > .fair-time {
        font-size: 24px;
        font-weight: 700;
        @media (max-width: 768px) {
          font-size: 18px;
        }
      }

      > .caution {
        font-size: 12px;
        color: ${styleTheme.colors.red.base};
      }
    }
  }
`
